import axios from 'axios';
import { API } from '../global/global';
import { TagBlacResponse, TagBlacEstadisticasResponse, TrazabilidadResponse } from '../interfaces/TagBlacInterface';

// Configuración de Blac API
const BLAC_API_URL = 'https://n1.ws.blacsol.com/v1';
const BLAC_CREDENTIALS = {
  username: 'Sky.Angel',
  password: 'aqvdE!p*3'
};

export interface BlacTokenResponse {
  token: string;
  expires_in?: number;
}

export interface BlacDevice {
  name_device: string;
  plate: string;
  vin: string;
  event: string;
  longitude: string;
  latitude: string;
  speed: string;
  odometer: string;
  address: string;
  dateTime: string;
}

export interface BlacDevicesResponse {
  username: string;
  data: BlacDevice[];
}

// Función para obtener el token de Blac a través del proxy del backend
export const getBlacToken = async (): Promise<string> => {
  console.log('🔄 Obteniendo token de Blac a través del proxy del backend...');

  try {
    // Hacer la petición a través del proxy en nuestro backend
    const response = await axios.post(`${API}/blac/token`, {}, {
      timeout: 15000
    });

    console.log('✅ Respuesta del proxy:', response.data);

    // Verificar que la respuesta sea exitosa
    if (response.data.status_code !== 200) {
      throw new Error(`Error del servidor Blac: ${response.data.status_code} - ${response.data.message}`);
    }

    const token = response.data.message;
    
    if (!token) {
      throw new Error('No se recibió token en la respuesta del proxy');
    }

    console.log('🎯 Token fresco obtenido a través del proxy');
    console.log('📏 Longitud del token:', token.length);
    console.log('🔍 Primeros 50 caracteres:', token.substring(0, 50) + '...');

    // Limpiar tokens anteriores y guardar el nuevo
    localStorage.removeItem('blac_token');
    localStorage.removeItem('blac_token_timestamp');
    
    localStorage.setItem('blac_token', token);
    localStorage.setItem('blac_available', 'true');
    localStorage.setItem('blac_token_timestamp', Date.now().toString());

    console.log('💾 Token fresco guardado en localStorage');
    return token;

  } catch (error) {
    console.error('❌ Error obteniendo token a través del proxy:', error);
    
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 404) {
        console.error('🚫 El endpoint del proxy no existe en el backend');
        console.error('💡 Necesitas agregar el endpoint /api/blac/token en tu backend');
        throw new Error('Proxy no implementado: Agrega el endpoint /api/blac/token en tu backend de Samsung');
      }
      
      if (error.response?.status === 500) {
        console.error('🔥 Error interno del proxy:', error.response.data);
        throw new Error(`Error del proxy: ${error.response.data?.error || 'Error interno'}`);
      }
    }
    
    localStorage.setItem('blac_available', 'false');
    throw error;
  }
};

// Función para obtener los dispositivos de Blac a través del proxy
export const getBlacDevices = async (): Promise<BlacDevice[]> => {
  console.log('🔄 Obteniendo dispositivos de Blac a través del proxy...');
  
  // Obtener el token de Blac del localStorage
  const blacToken = localStorage.getItem('blac_token');
  
  console.log('🔍 Token en localStorage:', blacToken ? `${blacToken.substring(0, 20)}...` : 'NO ENCONTRADO');
  
  if (!blacToken) {
    console.error('❌ No hay token de Blac disponible');
    throw new Error('Token de Blac no disponible. Por favor, inicia sesión nuevamente.');
  }

  console.log('🔑 Usando token de Blac para obtener dispositivos');
  console.log('📤 Enviando petición con token de longitud:', blacToken.length);

  try {
    // Hacer la petición a través del proxy en nuestro backend
    const response = await axios.post(`${API}/blac/devices`, {
      token: blacToken
    }, {
      timeout: 15000
    });

    console.log('✅ Respuesta del proxy para dispositivos:', response.data);

    // ✅ Manejar la estructura de respuesta de Blac: {username: "Sky.Angel", data: [...]}
    const responseData = response.data;
    
    // Verificar que la respuesta tenga la estructura esperada
    if (!responseData || typeof responseData !== 'object') {
      console.warn('⚠️ Respuesta inesperada del servidor:', responseData);
      return [];
    }

    // ✅ Extraer los dispositivos del campo 'data'
    const devices = responseData.data || [];
    
    // ✅ Log detallado según el resultado
    if (devices.length === 0) {
      console.log('📭 El proveedor devolvió un array vacío - esto es normal cuando hay fallas temporales del endpoint');
      console.log('🔍 Respuesta completa:', responseData);
      console.log('👤 Usuario:', responseData.username || 'desconocido');
    } else {
      console.log(`📱 ${devices.length} dispositivos de Blac obtenidos exitosamente`);
      console.log('👤 Usuario:', responseData.username || 'desconocido');
      console.log('🏷️ Dispositivos:', devices.map((d: BlacDevice) => ({
        name: d.name_device,
        plate: d.plate,
        lat: d.latitude,
        lng: d.longitude,
        event: d.event,
        speed: d.speed,
        address: d.address
      })));
    }
    
    return devices;
    
  } catch (error) {
    console.error('❌ Error obteniendo dispositivos a través del proxy:', error);
    
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      
      if (status === 403) {
        console.error('🔒 Token de Blac inválido o expirado');
        throw new Error('Token de Blac inválido. Por favor, inicia sesión nuevamente.');
      }
      
      if (status === 404) {
        console.error('🚫 El endpoint del proxy para dispositivos no existe');
        throw new Error('Endpoint de dispositivos no disponible');
      }
      
      if (status && status >= 500) {
        console.error('🔥 Error del servidor al obtener dispositivos');
        throw new Error('Error del servidor. Intenta nuevamente en unos momentos.');
      }
    }
    
    // Error de red o timeout
    if (axios.isAxiosError(error) && error.code === 'ECONNABORTED') {
      console.error('⏱️ Timeout al obtener dispositivos');
      throw new Error('Timeout al conectar con el servicio. Intenta nuevamente.');
    }
    
    throw new Error('Error al obtener dispositivos de Blac');
  }
};

// Función mock para desarrollo cuando no hay backend disponible
const getBlacDevicesMock = (): BlacDevice[] => {
  console.log('🧪 Usando dispositivos mock de Blac para desarrollo...');
  
  return [
    {
      name_device: "Prueba Sky - 006298",
      plate: "780901703006298",
      vin: "",
      event: "Posición",
      longitude: "-99.20392",
      latitude: "19.441433",
      speed: "0",
      odometer: "0",
      address: "Plaza Carso, Calle Lago Zurich, Plaza Carso, Miguel Hidalgo, Santa Fe, Ciudad de México, 11529, México",
      dateTime: "20250807120800"
    },
    {
      name_device: "Dispositivo Black - 001",
      plate: "ABC123",
      vin: "",
      event: "Posición",
      longitude: "-99.1332",
      latitude: "19.4326",
      speed: "25",
      odometer: "1500",
      address: "Centro Histórico, Ciudad de México, México",
      dateTime: "20250807130000"
    },
    {
      name_device: "Dispositivo Black - 002",
      plate: "XYZ789",
      vin: "",
      event: "Posición",
      longitude: "-99.1677",
      latitude: "19.4284",
      speed: "0",
      odometer: "2300",
      address: "Polanco, Miguel Hidalgo, Ciudad de México, México",
      dateTime: "20250807125500"
    }
  ];
};

// Función para verificar si el token de Blac está expirado
export const isBlacTokenExpired = (): boolean => {
  const timestamp = localStorage.getItem('blac_token_timestamp');
  if (!timestamp) return true;

  const tokenAge = Date.now() - parseInt(timestamp);
  const nineMinutes = 9 * 60 * 1000; // 9 minutos en milisegundos
  
  return tokenAge >= nineMinutes;
};

// Función para renovar el token de Blac si es necesario
export const refreshBlacTokenIfNeeded = async (): Promise<string | null> => {
  const currentToken = localStorage.getItem('blac_token');
  
  if (!currentToken || isBlacTokenExpired()) {
    try {
      return await getBlacToken();
    } catch (error) {
      console.error('Error renovando token de Blac:', error);
      return null;
    }
  }
  
  return currentToken;
};

// Hook personalizado para manejar el token de Blac automáticamente
export const useBlacTokenManager = () => {
  const startTokenRefresh = () => {
    // Renovar token cada 9 minutos
    const interval = setInterval(async () => {
      try {
        await refreshBlacTokenIfNeeded();
        console.log('Token de Blac renovado automáticamente');
      } catch (error) {
        console.error('Error en renovación automática del token de Blac:', error);
      }
    }, 9 * 60 * 1000); // 9 minutos

    return interval;
  };

  const stopTokenRefresh = (interval: NodeJS.Timeout) => {
    clearInterval(interval);
  };

  return { startTokenRefresh, stopTokenRefresh };
};

// ===== NUEVAS FUNCIONES PARA GESTIÓN DE TAGS ALMACENADOS =====

// Función para obtener todos los tags almacenados en la base de datos
export const getAllTags = async (page: number = 1, perPage: number = 10, search?: string): Promise<TagBlacResponse> => {
  console.log('🔄 Obteniendo tags almacenados desde la base de datos...');
  
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    });
    
    if (search && search.trim()) {
      params.append('search', search.trim());
    }

    // Obtener token del localStorage
    const token = localStorage.getItem('token');
    
    const response = await axios.get(`${API}/blac/tags?${params.toString()}`, {
      timeout: 10000,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });

    console.log('✅ Tags obtenidos exitosamente:', response.data);
    return response.data;
    
  } catch (error) {
    console.error('❌ Error obteniendo tags:', error);
    
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      if (status === 404) {
        throw new Error('Endpoint de tags no encontrado');
      }
      if (status && status >= 500) {
        throw new Error('Error del servidor al obtener tags');
      }
    }
    
    throw new Error('Error al obtener tags de la base de datos');
  }
};

// Función para obtener estadísticas de tags
export const getTagsEstadisticas = async (): Promise<TagBlacEstadisticasResponse> => {
  console.log('🔄 Obteniendo estadísticas de tags...');
  
  try {
    // Obtener token del localStorage
    const token = localStorage.getItem('token');
    
    const response = await axios.get(`${API}/blac/estadisticas`, {
      timeout: 10000,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });

    console.log('✅ Estadísticas obtenidas exitosamente:', response.data);
    return response.data;
    
  } catch (error) {
    console.error('❌ Error obteniendo estadísticas:', error);
    throw new Error('Error al obtener estadísticas de tags');
  }
};

// Función para obtener trazabilidad de un tag específico
export const getTagTrazabilidad = async (
  tagId: number, 
  fechaInicio?: string, 
  fechaFin?: string
): Promise<TrazabilidadResponse> => {
  console.log(`🔄 Obteniendo trazabilidad del tag ${tagId}...`);
  
  try {
    // ✅ CLAVE: Obtener token de BLAC antes de hacer la petición
    const token = await refreshBlacTokenIfNeeded();
    
    if (!token) {
      throw new Error('No se pudo obtener token de BLAC para trazabilidad');
    }
    
    console.log('🔑 Token de BLAC obtenido para trazabilidad, longitud:', token.length);
    
    const params = new URLSearchParams();
    
    if (fechaInicio) {
      params.append('fecha_inicio', fechaInicio);
    }
    if (fechaFin) {
      params.append('fecha_fin', fechaFin);
    }
    
    const url = `${API}/blac/tags/${tagId}/trazabilidad${params.toString() ? '?' + params.toString() : ''}`;
    
    const response = await axios.get(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      timeout: 15000
    });

    console.log('✅ Trazabilidad obtenida exitosamente:', response.data);
    return response.data;
    
  } catch (error) {
    console.error('❌ Error obteniendo trazabilidad:', error);
    
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      if (status === 404) {
        throw new Error('Tag no encontrado');
      }
      if (status && status >= 500) {
        throw new Error('Error del servidor al obtener trazabilidad');
      }
    }
    
    throw new Error('Error al obtener trazabilidad del tag');
  }
};

// Función para forzar sincronización manual
export const forzarSincronizacion = async (): Promise<{ success: boolean; message: string }> => {
  console.log('🔄 Forzando sincronización manual...');
  
  try {
    const response = await axios.post(`${API}/blac/sincronizar`, {}, {
      timeout: 30000 // 30 segundos para sincronización
    });

    console.log('✅ Sincronización completada:', response.data);
    return response.data;
    
  } catch (error) {
    console.error('❌ Error en sincronización:', error);
    
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      if (error.code === 'ECONNABORTED') {
        throw new Error('Timeout en sincronización. El proceso puede estar ejecutándose en segundo plano.');
      }
      if (status && status >= 500) {
        throw new Error('Error del servidor durante la sincronización');
      }
    }
    
    throw new Error('Error al ejecutar sincronización manual');
  }
};
